import { createLogger } from "utils/logging";
import { fetchRetry } from "utils/fetch";
import { DetailviewService } from "./DetailviewService";
import { TrackingService } from "./tracking/TrackingService";
import { captureErrorWithPayload } from "utils/error";
import { BenefitElement, defineElement } from "../BenefitElement";

const attributeNameVariationId = "variation-id";
const attributeNameProductId = "product-id";
const attributeNameEventMergeId = "event-merge-id";
const attributeNameOriginFeature = "feature";
const attributeNameTeam = "team";
const attributeNameIsPrefetched = "is-prefetched";

const searchParamBenefitId = "benefitId";
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const log = createLogger("tag", "detailview.component");
const DETAILVIEW_ELEMENT_NAME = "ft9-detailview-v1";

const ERROR_KEY = "ft9-benefit-detailview-not-ok";

export class DetailviewElement extends BenefitElement {
	private readonly detailviewService: DetailviewService;
	private autoActivateBenefitId: string | undefined;

	constructor() {
		super(); /*                                     */
		this.detailviewService = new DetailviewService(new TrackingService());
	}

	protected async onInit(): Promise<void> {
		if (this.getAttribute(attributeNameIsPrefetched) === "true") {
			log.info("Detailview is prefetched, skipping backend call.");
			this.initDetailview();
			return;
		}
		await this.callBackend();
	}

	getAutoactiveBenefitId() {
		/*                                              */
		return this.autoActivateBenefitId;
	}

	protected async callBackend() {
		this.replaceChildren(); /*                       */

		const variationId = this.getAttribute(attributeNameVariationId);
		if (!variationId) {
			log.error("No variationId provided, cannot load detailview.");
			captureErrorWithPayload("No variationId provided", "tag", "detailview", ERROR_KEY);
			return;
		}

		const benefitId = new URLSearchParams(window.location.search).get(searchParamBenefitId);
		if (benefitId && UUID_REGEX.test(benefitId)) {
			this.autoActivateBenefitId = benefitId;
		}

		const urlSearchParams = this.createSearchParams();
		log.info("Calling tag for %s with %s", this.id || "id-not-set", urlSearchParams.toString());

		try {
			const response = await fetchRetry({
				url: `/benefit-tag/detailview?${urlSearchParams}`,
				namespace: "detailview",
				fetchOptions: {
					headers: {
						Accept: "text/html;charset=UTF-8",
						"X-Requested-With": "XMLHttpRequest",
					},
					priority: "high",
				},
				retryOptions: {
					limit: 2,
					delay: 50,
				},
			});

			if (!response.ok) {
				log.error("detailview endpoint returns a non ok response!", response.status);

				const labels = {
					url_search_params: urlSearchParams.toString(),
					tries: 1,
				};

				captureErrorWithPayload("callBackend error", "tag", "detailview", ERROR_KEY, response.status, labels);
				return;
			}

			if (response.status === 200) {
				response.text().then((text) => {
					this.innerHTML = text;
					this.initDetailview();
				});
				return;
			}

			this.detailviewService.handleNoContentDetailview(variationId, this);
		} catch (error) {
			log.error("detailview request returned an error!", error);

			const labels = {
				url_search_params: urlSearchParams.toString(),
				tries: 3,
			};
			captureErrorWithPayload("callBackend error", "tag", "detailview", ERROR_KEY, error, labels);
		}
	}

	protected createSearchParams() {
		return this.buildSearchParams({
			variationId: this.getAttribute(attributeNameVariationId),
			productId: this.getAttribute(attributeNameProductId),
			team: this.getAttribute(attributeNameTeam),
			originFeature: this.getAttribute(attributeNameOriginFeature),
			benefitId: this.autoActivateBenefitId,
		});
	}

	private initDetailview() {
		const variationId = this.getAttribute(attributeNameVariationId);
		const eventMergeId = this.getAttribute(attributeNameEventMergeId);

		if (!variationId) {
			return;
		}

		this.detailviewService.initialize(variationId, this, eventMergeId);
	}
}

defineElement(DetailviewElement, DETAILVIEW_ELEMENT_NAME, "ftnine_emergency_custom_element_disable_detailview");
